import { useState, useCallback } from "react"
import { lumi } from "../lib/lumi"

// 호기(시리얼번호 부여받은 개별 단위)
export interface EquipmentUnit {
  unitId: string
  serialNumber: string
  unitName: string
}

export interface Equipment {
  _id: string
  productName: string
  deliveryDate?: string
  serialNumber?: string
  supplier?: string
  customerMgmtNumber?: string
  tags?: string[]
  units?: EquipmentUnit[]
  sortOrder?: number
  creator: string
  creatorName?: string
  creatorEmail?: string
  createdAt: string
  updatedAt: string
}

export interface EquipmentInput {
  productName: string
  deliveryDate?: string
  serialNumber?: string
  supplier?: string
  customerMgmtNumber?: string
  tags?: string[]
  units?: EquipmentUnit[]
}

export function useEquipments() {
  const [equipments, setEquipments] = useState<Equipment[]>([])
  const [loading, setLoading] = useState(false)

  const fetchEquipments = useCallback(async () => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.equipments.list({
        sort: { createdAt: -1 },
      })
      // sortOrder가 있으면 우선(오름차순), 없으면 createdAt 최신순(fallback)으로 정렬
      const sorted = ((list || []) as Equipment[]).slice().sort((a, b) => {
        const aHas = typeof a.sortOrder === "number"
        const bHas = typeof b.sortOrder === "number"
        if (aHas && bHas) return (a.sortOrder as number) - (b.sortOrder as number)
        if (aHas) return -1
        if (bHas) return 1
        return (b.createdAt || "").localeCompare(a.createdAt || "")
      })
      setEquipments(sorted)
      console.log("equipments fetched:", list?.length)
    } catch (error) {
      console.error("장비 목록 조회 실패:", error)
    } finally {
      setLoading(false)
    }
  }, [])

  const createEquipment = useCallback(
    async (
      data: EquipmentInput,
      meta: { creator: string; creatorName?: string; creatorEmail?: string }
    ) => {
      const now = new Date().toISOString()
      const payload = {
        productName: data.productName,
        deliveryDate: data.deliveryDate || "",
        serialNumber: data.serialNumber || "",
        supplier: data.supplier || "",
        customerMgmtNumber: data.customerMgmtNumber || "",
        tags: data.tags || [],
        units: data.units || [],
        creator: meta.creator,
        creatorName: meta.creatorName || "",
        creatorEmail: meta.creatorEmail || "",
        createdAt: now,
        updatedAt: now,
      }
      const created = await lumi.entities.equipments.create(payload)
      console.log("equipment created:", created?._id)
      await fetchEquipments()
      return created
    },
    [fetchEquipments]
  )

  const updateEquipment = useCallback(
    async (id: string, data: EquipmentInput) => {
      const payload = {
        productName: data.productName,
        deliveryDate: data.deliveryDate || "",
        serialNumber: data.serialNumber || "",
        supplier: data.supplier || "",
        customerMgmtNumber: data.customerMgmtNumber || "",
        tags: data.tags || [],
        units: data.units || [],
        updatedAt: new Date().toISOString(),
      }
      const updated = await lumi.entities.equipments.update(id, payload)
      console.log("equipment updated:", id)
      await fetchEquipments()
      return updated
    },
    [fetchEquipments]
  )

  // 호기 목록만 갱신 (시리얼번호 추가/삭제)
  const updateUnits = useCallback(
    async (id: string, units: EquipmentUnit[]) => {
      const updated = await lumi.entities.equipments.update(id, {
        units,
        updatedAt: new Date().toISOString(),
      })
      console.log("equipment units updated:", id, units.length)
      await fetchEquipments()
      return updated
    },
    [fetchEquipments]
  )

  // 장비 표시 순서 저장 (팀장급 이상만 호출). orderedIds 순서대로 sortOrder를 0,1,2...로 부여
  const updateOrder = useCallback(
    async (orderedIds: string[]) => {
      const now = new Date().toISOString()
      await Promise.all(
        orderedIds.map((id, index) =>
          lumi.entities.equipments.update(id, {
            sortOrder: index,
            updatedAt: now,
          })
        )
      )
      console.log("equipment order updated:", orderedIds.length)
      await fetchEquipments()
    },
    [fetchEquipments]
  )

  const deleteEquipment = useCallback(
    async (id: string) => {
      await lumi.entities.equipments.delete(id)
      console.log("equipment deleted:", id)
      await fetchEquipments()
    },
    [fetchEquipments]
  )

  return {
    equipments,
    loading,
    fetchEquipments,
    createEquipment,
    updateEquipment,
    updateUnits,
    updateOrder,
    deleteEquipment,
  }
}
